
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, RefreshCw, Save, Share2, CheckCircle, AlertCircle } from 'lucide-react';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AdminSiteConfigSocialMediaSettings = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  const socialPlatforms = [
    { key: 'twitter_url', label: 'Twitter/X URL', placeholder: 'https://twitter.com/yourhandle' },
    { key: 'facebook_url', label: 'Facebook URL', placeholder: 'https://facebook.com/yourpage' },
    { key: 'linkedin_url', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/yourcompany' },
    { key: 'instagram_url', label: 'Instagram URL', placeholder: 'https://instagram.com/yourhandle' },
    { key: 'youtube_url', label: 'YouTube URL', placeholder: 'https://youtube.com/@yourchannel' },
    { key: 'discord_url', label: 'Discord URL', placeholder: 'https://discord.gg/yourinvite' }
  ];

  const SocialPlatformCard = ({ platform }: { platform: typeof socialPlatforms[0] }) => {
    const { value, loading, refetch } = useSiteConfigValue(platform.key);
    const [localValue, setLocalValue] = useState('');
    const [hasChanged, setHasChanged] = useState(false);

    React.useEffect(() => {
      if (value !== null && value !== localValue) {
        setLocalValue(value);
        setHasChanged(false);
      }
    }, [value]);

    const handleInputChange = (newValue: string) => {
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || ''));
    };

    const handleUpdate = async () => {
      setSaving(platform.key);
      try {
        console.log(`Updating ${platform.key} to:`, localValue);
        
        const { error } = await supabase.rpc('update_site_config', {
          config_key: platform.key,
          new_value: localValue,
          admin_email: null,
          change_reason: `Updated ${platform.label} via Admin Panel`
        });

        if (error) throw error;

        toast({
          title: "Success",
          description: `${platform.label} updated successfully`,
        });
        
        setHasChanged(false);
        await refetch();
      } catch (error) {
        console.error(`Error updating ${platform.key}:`, error);
        toast({
          title: "Error",
          description: `Failed to update ${platform.label}`,
          variant: "destructive",
        });
      } finally {
        setSaving(null);
      }
    };

    const handleRefresh = async () => {
      console.log(`Refreshing ${platform.key}...`);
      await refetch();
    };

    const openUrl = () => {
      if (localValue) {
        window.open(localValue, '_blank');
      }
    };

    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm font-medium flex items-center justify-between">
            <span>{platform.label}</span>
            <div className="flex items-center space-x-2">
              {localValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openUrl}
                  className="text-blue-400 hover:text-blue-300 p-1"
                >
                  <ExternalLink className="w-3 h-3" />
                </Button>
              )}
              {loading ? (
                <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white text-xs">Current URL</Label>
              <div className="flex items-center space-x-1">
                {hasChanged && <Badge className="bg-orange-600/20 text-orange-400 text-xs">Modified</Badge>}
                {localValue ? (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-gray-400" />
                )}
              </div>
            </div>
            <Input
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={platform.placeholder}
              disabled={saving === platform.key}
              className="bg-gray-800 border-gray-600 text-white text-sm"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {loading ? 'Loading...' : `Loaded: ${value || 'Not set'}`}
            </div>
            <Button
              onClick={handleUpdate}
              disabled={!hasChanged || saving === platform.key}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {saving === platform.key ? (
                <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin mr-1" />
              ) : (
                <Save className="w-3 h-3 mr-1" />
              )}
              Update
            </Button>
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            <div>Debug Info:</div>
            <div>• Key: {platform.key}</div>
            <div>• Current: {value || 'null'}</div>
            <div>• Local: {localValue || 'empty'}</div>
            <div>• Changed: {hasChanged ? 'yes' : 'no'}</div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <Share2 className="w-5 h-5" />
          <span>Social Media Configuration</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {socialPlatforms.map((platform) => (
            <SocialPlatformCard key={platform.key} platform={platform} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigSocialMediaSettings;
